// ToneMirror Layers 2 (Rewrite Behaviour), 3 (Emotional Trend), and 4
// (Communication Drift).
//
// ADDITIVE module — does not modify or import `toneMirror.js`'s existing
// detectLayer2 / detectLayer3 / detectLayer4 / getToneMirrorState
// orchestrator. Both the old and new engines coexist and can be wired
// into the UI independently.
//
// Architecture:
//   • Detection = pure deterministic JS over sessionLog entries + improved
//     message bubbles. No LLM, no network.
//   • Outputs for the main flags (style_aligned / drift_soft /
//     drift_assertive / regenerate_pattern / improving / tension_rising /
//     consistent_aligned / off_style / aligned / drift_detected) live
//     inline as static (flag × style) maps below — they are NOT a phrase
//     library.
//   • The closed phrase library applies ONLY to the Register Insight,
//     Register Trend, and Register Drift templates and is imported from
//     registerPhraseLibrary.
//   • Per-register tracking is enforced: every detector groups entries by
//     `relationship_register`. Entries with `register=null` aggregate into
//     a synthetic `"_none_"` bucket so the engines still produce a
//     main-flag observation even when no register is set (graceful
//     degrade); only the register-specific templates are skipped.
//
// Style key conventions:
//   User-facing communication style keys (sessionLog.communication_style):
//     calm | direct | warm | professional
//   Internal rewrite style keys (message bubble .style):
//     gentle | concise | warm | firm | professional
//
// ──────────────────────────────────────────────────────────────────────
// INTER-LAYER PRIORITY (per spec)
// ──────────────────────────────────────────────────────────────────────
//   1. Layer 1 — always runs, overrides all in active session.
//      (Layer 1 lives in `lib/insights.js`, NOT here.)
//   2. Layer 2 — passive. Informs suggestions only. `detectRewriteBehaviour`.
//   3. Layer 3 — must complete before Layer 4 runs. `detectEmotionalTrend`.
//   4. Layer 4 — background only. Never interrupts rewrite.
//      `detectCommunicationDrift`.
//
// The UI is responsible for honoring this ordering; this module exposes
// each layer as an independent pure function so the caller controls the
// execution order. The `runAllLayers()` helper at the bottom of this file
// returns all four together in the correct sequence.
//
// ──────────────────────────────────────────────────────────────────────
// GLOBAL RULES (per spec — enforced at the call/render sites)
// ──────────────────────────────────────────────────────────────────────
//   • flag=none OR threshold not met → return active=false / output=null.
//   • One output line per layer per session — never a paragraph.
//   • LLM selects from phrase libraries only — never free-form. This
//     module never calls an LLM; all outputs are static or closed-pick.
//   • Template outputs fill from session-record data only.
//   • Register modifier adjusts delivery tone only (closed-library line
//     swap), never the underlying flag.
//   • No percentages, scores, or clinical language in user-facing output.
//   • No fabricated emotional states — flags map directly to spec lines.
//   • communication_style is always the user's free choice — Layer 4
//     never auto-changes it; it only invites the user to update it via
//     the special "Your style preference may have shifted" prompt.
//   • Insights are observational — never prescriptive.
//   • Single-session anomaly protection is active in Layer 3 + 4 —
//     tension_rising and drift_detected both require multi-session
//     evidence, never a single outlier.
//   • Drift fatigue suppression is active from session 10 onward (Layer 4).
//   • User never sees internal taxonomy terms: Authority, Intimate,
//     Familial, Peer. Closed phrase libraries are scrubbed of these.
//   • Local storage only — no backend.

import { readEligibleSessions } from "@/lib/sessionLog";
import {
    pickRegisterInsight,
    pickRegisterTrend,
    pickRegisterDrift,
} from "@/lib/registerPhraseLibrary";
import { REGISTER_KEYS, registerPhrase } from "@/lib/registers";

// ─── Style → expected accepted rewrite styles (Layer 2 baseline) ───────
// "Soft" rewrites = gentle + warm. "Assertive" rewrites = firm + concise.
const EXPECTED_REWRITES = {
    // Calm & Thoughtful → empathetic / soft / reflective rewrites accepted.
    calm: new Set(["gentle", "warm"]),
    // Direct & Concise → concise / assertive / neutral rewrites accepted.
    direct: new Set(["concise", "firm", "professional"]),
    // Warm & Friendly → warm / empathetic / appreciative rewrites accepted.
    warm: new Set(["warm", "gentle"]),
    // Professional & Neutral → structured / neutral / assertive rewrites accepted.
    professional: new Set(["professional", "firm"]),
};

const SOFT_REWRITES = new Set(["gentle", "warm"]);
const ASSERTIVE_REWRITES = new Set(["firm", "concise"]);

// ─── Style → on-track signals (Layer 3 trend baseline) ─────────────────
const ON_TRACK_SIGNALS = {
    calm: new Set(["empathetic", "constructive", "apologetic", "neutral"]),
    direct: new Set([
        "assertive",
        "constructive",
        "neutral",
        "solution_focused",
    ]),
    warm: new Set(["empathetic", "appreciative", "apologetic", "constructive"]),
    professional: new Set([
        "neutral",
        "constructive",
        "assertive",
        "solution_focused",
    ]),
};

const HIGH_RISK_LABEL = "🔴";

// ─── Layer 2 thresholds ────────────────────────────────────────────────
const LAYER2_MIN_SESSIONS = 5; // sessions since style_change_date
const STYLE_ALIGNED_PCT = 0.7; // ≥70% match → style_aligned
const DRIFT_MIN_COUNT = 3; // ≥3 off-baseline sessions → drift_*
const REGEN_PATTERN_MIN = 3; // ≥3 regens on same tone → regenerate_pattern

// ─── Layer 3 thresholds ────────────────────────────────────────────────
const LAYER3_MIN_SESSIONS = 7; // sessions since style_change_date
const TENSION_RISING_MIN_SIDS = 3; // distinct session_ids w/ risky tone
const IMPROVING_MIN_SIDS = 3; // distinct session_ids showing improvement

// ─── Layer 4 thresholds ────────────────────────────────────────────────
const LAYER4_MIN_SESSIONS = 10; // sessions since style_change_date
// Detection windows: on-track share %. ≥ ALIGNED_MIN_PCT → aligned;
// ≤ DRIFT_MAX_PCT → drift_detected; in-between → none.
const LAYER4_ALIGNED_MIN_PCT = 0.6;
const LAYER4_DRIFT_MAX_PCT = 0.4;
// Drift fatigue: if the engine showed drift_detected within the last
// FATIGUE_SUPPRESS_WINDOW sessions, suppress and increment the
// consecutive-suppression counter. After FATIGUE_PROMPT_AFTER consecutive
// suppressions, surface the "Your style preference may have shifted"
// one-shot prompt instead of the regular drift line.
const FATIGUE_SUPPRESS_WINDOW = 5;
const FATIGUE_PROMPT_AFTER = 3;
// localStorage key — namespaced so the existing legacy
// toneguard:lastDriftShown (used by lib/toneMirror.js's own drift
// detector) is NOT touched.
const LAYER4_STATE_KEY = "toneguard:layer4State";

// ─── Output libraries (static — NOT a phrase library) ──────────────────
// These come directly from the user's spec tables.
const LAYER2_OUTPUTS = {
    style_aligned: {
        calm: "Your rewrite choices reflect your calm, measured approach.",
        direct: "Your rewrite choices are consistent with your direct style.",
        warm: "Your rewrites stay true to your warm communication style.",
        professional:
            "Your rewrite choices align with your professional style.",
    },
    drift_soft: {
        direct:
            "You're accepting warmer rewrites than your Direct style suggests — intentional?",
        professional:
            "Your rewrite choices have been warmer than your Professional style — worth noting.",
    },
    drift_assertive: {
        calm:
            "You're accepting more direct rewrites — a shift from your usual style.",
        warm:
            "Your rewrite choices have been firmer than your Warm style — intentional?",
    },
    regenerate_pattern: {
        calm:
            "You tend to move away from that phrasing — noted for future suggestions.",
        direct:
            "You tend to move away from that phrasing — noted for future suggestions.",
        warm:
            "You tend to move away from that phrasing — noted for future suggestions.",
        professional:
            "You tend to move away from that phrasing — noted for future suggestions.",
    },
};

const LAYER3_OUTPUTS = {
    improving: {
        calm: "Your conversations have grown more reflective and measured.",
        direct: "Your messages have become cleaner and more purposeful.",
        warm: "Your conversations have felt more connected and warmer.",
        professional:
            "Your communication has become more structured and precise.",
    },
    tension_rising: {
        calm:
            "Your recent messages have felt less settled than your usual tone.",
        direct: "Your directness has edged into pressure — worth a pause.",
        warm: "Some warmth has slipped from your recent messages.",
        professional: "Your tone has been less neutral than usual lately.",
    },
    consistent_aligned: {
        calm: "Your tone has stayed calm and considered.",
        direct: "Your messages have stayed clear and on-point.",
        warm: "Your warmth has been consistent and genuine.",
        professional:
            "Your communication has remained balanced and professional.",
    },
    off_style: {
        calm: "Your recent tone has felt more reactive than reflective.",
        direct:
            "Your messages have been less direct — more hedging than usual.",
        warm: "Your messages have felt more transactional than warm.",
        professional:
            "Your tone has been more emotionally charged than usual.",
    },
};

const LAYER4_OUTPUTS = {
    drift_detected: {
        calm:
            "Your recent tone has been more reactive than your calm, thoughtful style.",
        direct:
            "Your recent messages have been less direct — more hedging than usual.",
        warm:
            "Your recent communication has felt more transactional than warm.",
        professional:
            "Your tone has been more emotionally charged than your neutral style.",
    },
    aligned: {
        calm:
            "Your communication has stayed true to your calm, thoughtful approach.",
        direct:
            "Your messages have remained clear and direct — consistent with your style.",
        warm: "Your warmth and care have come through consistently.",
        professional:
            "Your tone has stayed professional and measured — right where you want it.",
    },
};

// One-shot prompt surfaced ONLY after FATIGUE_PROMPT_AFTER consecutive
// suppressions. Never repeats until the user changes communication_style.
const LAYER4_SHIFT_PROMPT =
    "Your style preference may have shifted — would you like to update it?";

// ─── Helpers ────────────────────────────────────────────────────────────

const SYNTHETIC_NULL_REGISTER = "_none_";

const normalizeRegister = (r) =>
    r && REGISTER_KEYS.includes(r) ? r : SYNTHETIC_NULL_REGISTER;

const groupByRegister = (items, getRegister) => {
    const buckets = {};
    for (const item of items) {
        const key = normalizeRegister(getRegister(item));
        if (!buckets[key]) buckets[key] = [];
        buckets[key].push(item);
    }
    return buckets;
};

// Group eligible sessionLog entries by relationship_register. Entries
// with null register aggregate into a synthetic `_none_` bucket so we can
// still surface a main-flag observation (graceful degrade).
function groupSessionsByRegister(entries) {
    return groupByRegister(entries, (e) => e.relationship_register);
}

// Collect every "accepted rewrite" (improved bubble) from across all
// chats, carrying along its chat's register + the user's communication_style
// at the time the bubble was created. The bubble's `.style` is the
// internal rewrite style key (gentle / concise / warm / firm / professional).
function collectAcceptedRewrites(store, styleChangeDate = null) {
    const out = [];
    const floor = styleChangeDate ? new Date(styleChangeDate).getTime() : 0;
    for (const chat of store?.chats || []) {
        for (const msg of chat.messages || []) {
            if (msg.variant !== "improved") continue;
            const ts = msg.createdAt ? new Date(msg.createdAt).getTime() : 0;
            if (ts < floor) continue;
            out.push({
                style: msg.style || null,
                originalSignal: msg.originalSignal || null,
                originalRisk: msg.originalRisk || null,
                intent: msg.intent || null,
                createdAt: msg.createdAt || null,
                register: chat.relationship_register || null,
            });
        }
    }
    return out.sort(
        (a, b) =>
            new Date(a.createdAt || 0).getTime() -
            new Date(b.createdAt || 0).getTime(),
    );
}

// Tally session_id "improvement signals" — used by Layer 3 to count how
// many distinct sessions trended on-track vs. high-risk.
function classifyEntryForLayer3(entry, userStyle) {
    const isHighRisk = entry.tone_label === HIGH_RISK_LABEL;
    const onTrackSet = ON_TRACK_SIGNALS[userStyle] || null;
    const isOnTrack =
        !!onTrackSet &&
        !!entry.dominant_signal &&
        onTrackSet.has(entry.dominant_signal);
    return { isHighRisk, isOnTrack };
}

// ─── Layer 2: Rewrite Behaviour Engine ─────────────────────────────────
//
// Threshold: 5 sessions from style_change_date.
// Tracks patterns per register separately.
//
// For each register bucket (incl. the synthetic _none_ bucket):
//   1. Match accepted-rewrite styles against the user's style baseline.
//   2. Detect drift_soft / drift_assertive when ≥3 sessions sit OFF
//      the baseline in the "soft" or "assertive" direction respectively.
//   3. style_aligned fires when ≥70% of accepted rewrites match the
//      expected set for the user's current style.
//   4. regenerate_pattern fires when ≥3 accepted rewrites in the bucket
//      share the same dominant_signal (read off sessionLog entries that
//      pair with the chat by register + timestamp window).
//
// Returns per-register results, plus an optional registerInsight phrase
// when style_aligned is true in 2+ registers.

function detectLayer2PerRegister({
    sessionsByRegister,
    rewritesByRegister,
    userStyle,
}) {
    const results = {};
    const expected = EXPECTED_REWRITES[userStyle] || null;

    for (const reg of Object.keys(rewritesByRegister)) {
        const rewrites = rewritesByRegister[reg] || [];
        const sessions = sessionsByRegister[reg] || [];

        if (sessions.length < LAYER2_MIN_SESSIONS || !expected) {
            results[reg] = { flag: "none", meta: { sessions: sessions.length } };
            continue;
        }

        // Style-aligned check: % of accepted rewrites whose internal style
        // sits inside the expected set.
        const acceptedStyles = rewrites
            .filter((r) => !!r.style)
            .map((r) => r.style);
        const totalAccepted = acceptedStyles.length;
        const aligned = acceptedStyles.filter((s) => expected.has(s)).length;
        const alignedPct = totalAccepted === 0 ? 0 : aligned / totalAccepted;

        // Drift counts.
        const softCount = acceptedStyles.filter((s) =>
            SOFT_REWRITES.has(s),
        ).length;
        const assertiveCount = acceptedStyles.filter((s) =>
            ASSERTIVE_REWRITES.has(s),
        ).length;

        // Regenerate pattern: same dominant_signal ≥3 sessions in this
        // register bucket. (Best-effort — we don't have explicit regen
        // events in sessionLog yet, so we approximate by counting repeated
        // dominant_signal across distinct sessions.)
        const sigCounts = new Map();
        for (const s of sessions) {
            if (!s.dominant_signal) continue;
            sigCounts.set(
                s.dominant_signal,
                (sigCounts.get(s.dominant_signal) || 0) + 1,
            );
        }
        const topSig = [...sigCounts.entries()].sort((a, b) => b[1] - a[1])[0];
        const regenPattern = topSig && topSig[1] >= REGEN_PATTERN_MIN;

        // Flag resolution. Drift checks win over alignment when they fire,
        // but regenerate_pattern is reported as a separate, non-exclusive
        // observation when present.
        let flag = "none";
        if (
            (userStyle === "direct" || userStyle === "professional") &&
            softCount >= DRIFT_MIN_COUNT
        ) {
            flag = "drift_soft";
        } else if (
            (userStyle === "calm" || userStyle === "warm") &&
            assertiveCount >= DRIFT_MIN_COUNT
        ) {
            flag = "drift_assertive";
        } else if (alignedPct >= STYLE_ALIGNED_PCT && totalAccepted > 0) {
            flag = "style_aligned";
        }

        results[reg] = {
            flag,
            meta: {
                sessions: sessions.length,
                acceptedRewrites: totalAccepted,
                alignedPct,
                softCount,
                assertiveCount,
                regenPattern,
                topSignal: topSig ? topSig[0] : null,
                topSignalCount: topSig ? topSig[1] : 0,
            },
        };
    }

    return results;
}

function buildRegisterInsight(perRegister, seed) {
    // Fires when style_aligned in 2+ named (non-null) registers.
    const aligned = Object.entries(perRegister)
        .filter(
            ([reg, r]) =>
                reg !== SYNTHETIC_NULL_REGISTER && r.flag === "style_aligned",
        )
        .map(([reg]) => reg);
    if (aligned.length < 2) return null;
    // Pick the two with the highest accepted-rewrite counts so the
    // comparison rests on the strongest data.
    const sorted = aligned.sort(
        (a, b) =>
            (perRegister[b].meta.acceptedRewrites || 0) -
            (perRegister[a].meta.acceptedRewrites || 0),
    );
    const [a, b] = sorted;
    return pickRegisterInsight({ registerA: a, registerB: b, seed });
}

/**
 * Public entry point for Layer 2.
 *
 * @param {object} args
 * @param {object} args.store        Multi-chat store (from chats.loadStore).
 * @param {string} args.currentStyle User-facing communication style key.
 * @returns {{
 *   layer: 'layer2',
 *   active: boolean,
 *   perRegister: { [register]: { flag, meta, output } },
 *   registerInsight: string | null,
 *   summary: { totalSessions, eligibleSessions, totalAcceptedRewrites }
 * }}
 */
export function detectRewriteBehaviour({ store, currentStyle } = {}) {
    const eligible = readEligibleSessions();
    const totalAccepted = (store?.chats || []).reduce(
        (sum, c) =>
            sum +
            (c.messages || []).filter((m) => m.variant === "improved").length,
        0,
    );

    // Cold-start: don't fire until we have ≥5 eligible sessions overall.
    if (
        !currentStyle ||
        !EXPECTED_REWRITES[currentStyle] ||
        eligible.length < LAYER2_MIN_SESSIONS
    ) {
        return {
            layer: "layer2",
            active: false,
            perRegister: {},
            registerInsight: null,
            summary: {
                totalSessions: eligible.length,
                eligibleSessions: eligible.length,
                totalAcceptedRewrites: totalAccepted,
            },
        };
    }

    const sessionsByRegister = groupSessionsByRegister(eligible);
    const styleChangeDate = eligible[0]?.style_change_date || null;
    const rewrites = collectAcceptedRewrites(store, styleChangeDate);
    const rewritesByRegister = groupByRegister(rewrites, (r) => r.register);

    // Ensure every register the user has tagged gets a slot, even if it
    // has 0 accepted rewrites yet (so the UI knows to render an "Still
    // learning" hint per register rather than skip silently).
    for (const reg of Object.keys(sessionsByRegister)) {
        if (!rewritesByRegister[reg]) rewritesByRegister[reg] = [];
    }

    const perRegister = detectLayer2PerRegister({
        sessionsByRegister,
        rewritesByRegister,
        userStyle: currentStyle,
    });

    // Attach static outputs.
    for (const reg of Object.keys(perRegister)) {
        const r = perRegister[reg];
        const styleOutputs = LAYER2_OUTPUTS[r.flag] || null;
        r.output = styleOutputs ? styleOutputs[currentStyle] || null : null;
        // regenerate_pattern is a non-exclusive observation that can ride
        // alongside style_aligned / drift_*; surface it as a secondary line
        // when present.
        if (r.meta.regenPattern) {
            r.secondaryOutput =
                LAYER2_OUTPUTS.regenerate_pattern[currentStyle] || null;
        }
    }

    const seed = eligible.length;
    const registerInsight = buildRegisterInsight(perRegister, seed);

    return {
        layer: "layer2",
        active: true,
        perRegister,
        registerInsight,
        summary: {
            totalSessions: eligible.length,
            eligibleSessions: eligible.length,
            totalAcceptedRewrites: totalAccepted,
        },
    };
}

// ─── Layer 3: Emotional Trend Engine ───────────────────────────────────
//
// Threshold: 7 sessions from style_change_date.
// Tracks per register separately.
//
// Single-session anomaly rule: a single high-risk session is noise, not
// a pattern. tension_rising requires 3+ distinct session_ids.

function detectLayer3PerRegister({ sessionsByRegister, userStyle }) {
    const results = {};
    const onTrackSet = ON_TRACK_SIGNALS[userStyle] || null;

    for (const reg of Object.keys(sessionsByRegister)) {
        const entries = sessionsByRegister[reg] || [];
        if (entries.length < LAYER3_MIN_SESSIONS || !onTrackSet) {
            results[reg] = { flag: "none", meta: { sessions: entries.length } };
            continue;
        }

        // Distinct session_ids that carry high-risk / on-track signals.
        const highRiskSids = new Set();
        const onTrackSids = new Set();
        const offTrackSids = new Set();
        for (const e of entries) {
            const cls = classifyEntryForLayer3(e, userStyle);
            if (cls.isHighRisk && e.session_id)
                highRiskSids.add(e.session_id);
            if (cls.isOnTrack && e.session_id) onTrackSids.add(e.session_id);
            if (
                !cls.isOnTrack &&
                !cls.isHighRisk &&
                e.dominant_signal &&
                e.session_id
            ) {
                offTrackSids.add(e.session_id);
            }
        }

        // Improvement detection: split window in half, compare high-risk
        // count vs. on-track count between the halves.
        const half = Math.floor(entries.length / 2);
        const older = entries.slice(0, half);
        const recent = entries.slice(half);
        const countRiskOn = (arr) => {
            let risk = 0;
            let track = 0;
            const sidsRisk = new Set();
            const sidsTrack = new Set();
            for (const e of arr) {
                const cls = classifyEntryForLayer3(e, userStyle);
                if (cls.isHighRisk) {
                    risk += 1;
                    if (e.session_id) sidsRisk.add(e.session_id);
                }
                if (cls.isOnTrack) {
                    track += 1;
                    if (e.session_id) sidsTrack.add(e.session_id);
                }
            }
            return {
                risk,
                track,
                sidsRisk: sidsRisk.size,
                sidsTrack: sidsTrack.size,
            };
        };
        const olderTotals = countRiskOn(older);
        const recentTotals = countRiskOn(recent);

        // Flag resolution — first match wins, ordered by importance.
        let flag = "none";

        const riskDecreasing = recentTotals.risk < olderTotals.risk;
        const trackIncreasing = recentTotals.track > olderTotals.track;
        const distinctImprovementSids =
            recentTotals.sidsTrack + olderTotals.sidsTrack;

        if (
            riskDecreasing &&
            trackIncreasing &&
            distinctImprovementSids >= IMPROVING_MIN_SIDS
        ) {
            flag = "improving";
        } else if (highRiskSids.size >= TENSION_RISING_MIN_SIDS) {
            flag = "tension_rising";
        } else if (
            onTrackSids.size >= Math.ceil(entries.length / 2) &&
            offTrackSids.size <= 1
        ) {
            flag = "consistent_aligned";
        } else if (offTrackSids.size > onTrackSids.size) {
            flag = "off_style";
        }

        results[reg] = {
            flag,
            meta: {
                sessions: entries.length,
                highRiskSessionIds: highRiskSids.size,
                onTrackSessionIds: onTrackSids.size,
                offTrackSessionIds: offTrackSids.size,
                olderHalf: olderTotals,
                recentHalf: recentTotals,
            },
        };
    }

    return results;
}

function buildRegisterTrend(perRegister, seed) {
    // Fires when trend data exists across 2+ named registers AND at least
    // one has a non-none flag we can phrase.
    const named = Object.entries(perRegister)
        .filter(([reg]) => reg !== SYNTHETIC_NULL_REGISTER)
        .filter(([, r]) => r.flag !== "none");
    if (named.length < 2) return null;

    // Pick the register with the most session evidence so the comparison
    // surfaces from the strongest dataset.
    const sorted = named.sort(
        (a, b) => (b[1].meta.sessions || 0) - (a[1].meta.sessions || 0),
    );
    const [register, r] = sorted[0];
    return pickRegisterTrend({ register, trend: r.flag, seed });
}

/**
 * Public entry point for Layer 3.
 *
 * @param {object} args
 * @param {string} args.currentStyle User-facing communication style key.
 * @returns {{
 *   layer: 'layer3',
 *   active: boolean,
 *   perRegister: { [register]: { flag, meta, output } },
 *   registerTrend: string | null,
 *   summary: { eligibleSessions }
 * }}
 */
export function detectEmotionalTrend({ currentStyle } = {}) {
    const eligible = readEligibleSessions();

    if (
        !currentStyle ||
        !ON_TRACK_SIGNALS[currentStyle] ||
        eligible.length < LAYER3_MIN_SESSIONS
    ) {
        return {
            layer: "layer3",
            active: false,
            perRegister: {},
            registerTrend: null,
            summary: { eligibleSessions: eligible.length },
        };
    }

    const sessionsByRegister = groupSessionsByRegister(eligible);
    const perRegister = detectLayer3PerRegister({
        sessionsByRegister,
        userStyle: currentStyle,
    });

    for (const reg of Object.keys(perRegister)) {
        const r = perRegister[reg];
        const styleOutputs = LAYER3_OUTPUTS[r.flag] || null;
        r.output = styleOutputs ? styleOutputs[currentStyle] || null : null;
    }

    const seed = eligible.length;
    const registerTrend = buildRegisterTrend(perRegister, seed);

    return {
        layer: "layer3",
        active: true,
        perRegister,
        registerTrend,
        summary: { eligibleSessions: eligible.length },
    };
}

// ─── Layer 4: Communication Drift Engine ───────────────────────────────
//
// Threshold: 10 sessions from style_change_date.
// Runs AFTER Layer 3 completes — the caller MUST pass the Layer 3 state
// in so we never run Layer 4 against stale or absent trend data.
//
// Compares the user's communication_style against the dominant signal
// pattern across the eligible window:
//   • dominant signals match style on-track list → flag: aligned
//   • dominant signals don't match              → flag: drift_detected
//   • no clear pattern                          → flag: none
//
// Drift fatigue (spec):
//   Before displaying drift_detected:
//     • Check `last_drift_shown` in the session record.
//     • If the most recent display sits within the last 5 sessions →
//       suppress this output and increment a consecutive-suppression
//       counter.
//     • After 3 consecutive suppressions, show ONCE:
//       "Your style preference may have shifted — would you like to
//        update it?"  Never repeated until the user changes style.
//     • Update last_drift_shown on every actual display.
//
// State is stored in localStorage under LAYER4_STATE_KEY, namespaced so
// the legacy `toneguard:lastDriftShown` key (used by lib/toneMirror.js's
// own drift detector) is untouched.

function readLayer4State() {
    if (typeof window === "undefined") return null;
    try {
        const raw = window.localStorage.getItem(LAYER4_STATE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") return parsed;
    } catch {
        /* silent — quota / private-mode */
    }
    return null;
}

function writeLayer4State(state) {
    if (typeof window === "undefined") return;
    try {
        window.localStorage.setItem(
            LAYER4_STATE_KEY,
            JSON.stringify(state || {}),
        );
    } catch {
        /* silent */
    }
}

function defaultLayer4State(styleChangeDate) {
    return {
        styleChangeDate: styleChangeDate || null,
        lastShownSessionId: null,
        // Most-recent session_id the engine has actually PROCESSED (whether
        // it surfaced drift, suppressed it, or stayed aligned). Guards
        // against React StrictMode double-renders and same-render re-mounts
        // bumping the suppress counter for the same eligible window.
        lastEvaluatedSessionId: null,
        // Cached output from the last evaluation — replayed on idempotent
        // re-renders so the UI stays stable without re-mutating state.
        lastOutput: null,
        lastFlag: null,
        lastRegisterDrift: null,
        lastShiftPrompt: null,
        suppressCount: 0,
        shiftPromptShownAt: null, // ISO when the one-shot prompt fired
    };
}

// Reset Layer 4 state if the user changed communication_style — every
// counter is anchored to a single style choice and must not bleed into
// the next.
function resolveLayer4State(currentStyleChangeDate) {
    const existing = readLayer4State();
    if (
        !existing ||
        !existing.styleChangeDate ||
        existing.styleChangeDate !== currentStyleChangeDate
    ) {
        return defaultLayer4State(currentStyleChangeDate);
    }
    return {
        ...defaultLayer4State(currentStyleChangeDate),
        ...existing,
    };
}

// Identify the source register (highest off-track share) and the most
// aligned register (highest on-track share) — used by the Register Drift
// template. Returns null when fewer than 2 named registers have data.
function identifyDriftSourceAndAligned(eligibleEntries, userStyle) {
    const onTrackSet = ON_TRACK_SIGNALS[userStyle] || null;
    if (!onTrackSet) return null;
    const byReg = groupSessionsByRegister(eligibleEntries);
    const stats = [];
    for (const [reg, entries] of Object.entries(byReg)) {
        if (reg === SYNTHETIC_NULL_REGISTER) continue;
        if (entries.length === 0) continue;
        let on = 0;
        for (const e of entries) {
            if (e.dominant_signal && onTrackSet.has(e.dominant_signal)) {
                on += 1;
            }
        }
        stats.push({
            register: reg,
            onPct: on / entries.length,
            total: entries.length,
        });
    }
    if (stats.length < 2) return null;
    stats.sort((a, b) => a.onPct - b.onPct);
    const source = stats[0]; // lowest on-track share = driver of drift
    const aligned = stats[stats.length - 1]; // highest on-track share
    if (source.register === aligned.register) return null;
    // Require a meaningful gap so we don't surface drift attribution on
    // near-equal registers.
    if (aligned.onPct - source.onPct < 0.2) return null;
    return { source, aligned };
}

/**
 * Public entry point for Layer 4 — Communication Drift Engine.
 *
 * @param {object} args
 * @param {string} args.currentStyle   User-facing communication style key.
 * @param {object} [args.layer3State]  Output of detectEmotionalTrend().
 *                                     Pass it in so Layer 4 only runs after
 *                                     Layer 3 has finished (per spec).
 * @returns {{
 *   layer: 'layer4',
 *   active: boolean,
 *   flag: 'aligned' | 'drift_detected' | 'none',
 *   output: string | null,
 *   suppressed: boolean,
 *   shiftPrompt: string | null,
 *   registerDrift: string | null,
 *   meta: { onTrackPct, eligibleSessions, suppressCount, lastShownSessionId },
 * }}
 */
export function detectCommunicationDrift({
    currentStyle,
    layer3State = null,
} = {}) {
    const eligible = readEligibleSessions();

    // Cold start / threshold guards.
    if (
        !currentStyle ||
        !ON_TRACK_SIGNALS[currentStyle] ||
        eligible.length < LAYER4_MIN_SESSIONS
    ) {
        return {
            layer: "layer4",
            active: false,
            flag: "none",
            output: null,
            suppressed: false,
            shiftPrompt: null,
            registerDrift: null,
            meta: {
                onTrackPct: 0,
                eligibleSessions: eligible.length,
                suppressCount: 0,
                lastShownSessionId: null,
            },
        };
    }

    // Inter-layer priority: Layer 4 only runs after Layer 3 completes.
    // If the caller passed a Layer 3 state that wasn't active, we still
    // run (Layer 3 may be inactive simply because data is sparse) — but
    // we tighten the threshold by requiring layer3State.active OR enough
    // eligible sessions to stand alone. Both branches pass here at ≥10.
    void layer3State;

    const onTrackSet = ON_TRACK_SIGNALS[currentStyle];
    let onTrackHits = 0;
    let scored = 0;
    for (const e of eligible) {
        if (!e.dominant_signal) continue;
        scored += 1;
        if (onTrackSet.has(e.dominant_signal)) onTrackHits += 1;
    }
    const onTrackPct = scored === 0 ? 0 : onTrackHits / scored;

    // Resolve flag from the on-track share.
    let flag = "none";
    if (scored > 0) {
        if (onTrackPct >= LAYER4_ALIGNED_MIN_PCT) flag = "aligned";
        else if (onTrackPct <= LAYER4_DRIFT_MAX_PCT) flag = "drift_detected";
    }

    // Pull the style_change_date from the most recent eligible entry
    // (every entry stamps it at log time, so the most recent is the
    // authoritative current value).
    const styleChangeDate =
        eligible[eligible.length - 1]?.style_change_date ||
        eligible[0]?.style_change_date ||
        null;

    const state = resolveLayer4State(styleChangeDate);
    const mostRecent = eligible[eligible.length - 1];
    const mostRecentSid = mostRecent?.session_id || null;

    // ─── Idempotency guard ─────────────────────────────────────────────
    // If the engine has already evaluated this exact mostRecentSid, replay
    // the cached output WITHOUT mutating state. This makes the function
    // safe to call multiple times per render (React StrictMode), per
    // dashboard tab focus, or anywhere else the consumer needs to recompute
    // without flipping the drift-fatigue counter.
    if (
        mostRecentSid &&
        state.lastEvaluatedSessionId === mostRecentSid &&
        state.styleChangeDate === styleChangeDate
    ) {
        return {
            layer: "layer4",
            active: true,
            flag: state.lastFlag || "none",
            output: state.lastOutput || null,
            // Suppressed lines are also part of the cached snapshot — if
            // the last live evaluation suppressed, the replay must too.
            suppressed:
                state.lastFlag === "drift_detected" && !state.lastOutput,
            shiftPrompt: state.lastShiftPrompt || null,
            registerDrift: state.lastRegisterDrift || null,
            meta: {
                onTrackPct,
                eligibleSessions: eligible.length,
                suppressCount: state.suppressCount,
                lastShownSessionId: state.lastShownSessionId,
            },
        };
    }

    // Default: no suppression, no prompt.
    let suppressed = false;
    let output = null;
    let shiftPrompt = null;
    let registerDrift = null;
    const nextState = { ...state, lastEvaluatedSessionId: mostRecentSid };

    if (flag === "aligned") {
        output = LAYER4_OUTPUTS.aligned[currentStyle] || null;
        // aligned resets the suppression counter so the next genuine
        // drift_detected starts fresh.
        nextState.suppressCount = 0;
    } else if (flag === "drift_detected") {
        // Drift fatigue: was a drift already shown within the last 5
        // eligible sessions?
        const lastIdx = state.lastShownSessionId
            ? eligible.findIndex(
                  (e) => e.session_id === state.lastShownSessionId,
              )
            : -1;
        const sessionsSinceLast =
            lastIdx === -1 ? Infinity : eligible.length - 1 - lastIdx;

        if (sessionsSinceLast < FATIGUE_SUPPRESS_WINDOW) {
            suppressed = true;
            nextState.suppressCount = (state.suppressCount || 0) + 1;

            // After 3 consecutive suppressions, surface the one-shot
            // "your style preference may have shifted" prompt — but only
            // if it hasn't already fired since the last style change.
            if (
                nextState.suppressCount >= FATIGUE_PROMPT_AFTER &&
                !state.shiftPromptShownAt
            ) {
                shiftPrompt = LAYER4_SHIFT_PROMPT;
                nextState.shiftPromptShownAt = new Date().toISOString();
                nextState.suppressCount = 0;
            }
        } else {
            // No fatigue — surface the regular drift line and anchor
            // lastShown to the current most recent session_id.
            output = LAYER4_OUTPUTS.drift_detected[currentStyle] || null;
            nextState.lastShownSessionId = mostRecentSid;
            nextState.suppressCount = 0;

            const sourceInfo = identifyDriftSourceAndAligned(
                eligible,
                currentStyle,
            );
            if (sourceInfo) {
                registerDrift = pickRegisterDrift({
                    sourceRegister: sourceInfo.source.register,
                    alignedRegister: sourceInfo.aligned.register,
                    seed: eligible.length,
                });
            }
        }
    }

    // Cache the rendered output snapshot — replayed on subsequent
    // same-session calls via the idempotency guard above.
    nextState.lastFlag = flag;
    nextState.lastOutput = output;
    nextState.lastRegisterDrift = registerDrift;
    nextState.lastShiftPrompt = shiftPrompt;
    writeLayer4State(nextState);

    return {
        layer: "layer4",
        active: true,
        flag,
        output,
        suppressed,
        shiftPrompt,
        registerDrift,
        meta: {
            onTrackPct,
            eligibleSessions: eligible.length,
            suppressCount: nextState.suppressCount,
            lastShownSessionId: nextState.lastShownSessionId,
        },
    };
}

// ─── Inter-layer orchestrator (additive — does NOT replace
// getToneMirrorState from lib/toneMirror.js) ───────────────────────────
//
// Runs the new Layer 2 → Layer 3 → Layer 4 sequence in the order the spec
// mandates. Layer 1 lives in lib/insights.js and is the caller's
// responsibility — per spec, Layer 1 "always runs and overrides all in
// active session", which is outside this module's scope.
export function runAllLayers({ store, currentStyle } = {}) {
    const layer2 = detectRewriteBehaviour({ store, currentStyle });
    const layer3 = detectEmotionalTrend({ currentStyle });
    // Layer 4 runs AFTER Layer 3 — pass the state in so the dependency
    // is explicit at the call site.
    const layer4 = detectCommunicationDrift({
        currentStyle,
        layer3State: layer3,
    });
    return { layer2, layer3, layer4 };
}

// ─── Helper for the UI: list of per-register observations ──────────────
//
// Returns a flat list of `{ register, registerPhrase, primary, secondary }`
// entries the UI can render under each layer card. The `_none_` bucket is
// labelled as "all chats" so users without registers still see a card.
export function flattenRegisterObservations(perRegister) {
    return Object.entries(perRegister).map(([reg, r]) => {
        const isNamed = reg !== SYNTHETIC_NULL_REGISTER;
        return {
            register: isNamed ? reg : null,
            registerPhrase: isNamed ? registerPhrase(reg) : "all chats",
            flag: r.flag,
            primary: r.output || null,
            secondary: r.secondaryOutput || null,
            meta: r.meta,
        };
    });
}

// Exported for tests.
export const __internals = {
    EXPECTED_REWRITES,
    SOFT_REWRITES,
    ASSERTIVE_REWRITES,
    ON_TRACK_SIGNALS,
    LAYER2_OUTPUTS,
    LAYER3_OUTPUTS,
    LAYER4_OUTPUTS,
    LAYER4_SHIFT_PROMPT,
    LAYER2_MIN_SESSIONS,
    LAYER3_MIN_SESSIONS,
    LAYER4_MIN_SESSIONS,
    LAYER4_ALIGNED_MIN_PCT,
    LAYER4_DRIFT_MAX_PCT,
    FATIGUE_SUPPRESS_WINDOW,
    FATIGUE_PROMPT_AFTER,
    LAYER4_STATE_KEY,
    STYLE_ALIGNED_PCT,
    DRIFT_MIN_COUNT,
    REGEN_PATTERN_MIN,
    TENSION_RISING_MIN_SIDS,
    IMPROVING_MIN_SIDS,
    SYNTHETIC_NULL_REGISTER,
    detectLayer2PerRegister,
    detectLayer3PerRegister,
    classifyEntryForLayer3,
    collectAcceptedRewrites,
    groupSessionsByRegister,
    identifyDriftSourceAndAligned,
    readLayer4State,
    writeLayer4State,
    resolveLayer4State,
};
